import { useState, useEffect } from "react";
import { slugify } from "@/utils/productSlug";
import { Plus, Lock, Clock, AlertTriangle } from "lucide-react";
import { trackViewProduct } from "@/utils/tracking";
import { bcTrackProductView } from "@/utils/behavioralCommerce";
import { formatPrice, CURRENCIES } from "@/utils/localization";
import { AllergenBadges } from "./AllergenComponents";
import { ProductMediaThumb } from "./ProductMediaThumb";
import { formatAvailabilityMessage, formatAvailabilityBadge } from "@/utils/availabilityFormatter";
import ProductDetailModal from "./ProductDetailModal";

const fmtPrice = (amount, currency = "EUR") => {
  if (typeof amount !== "number" || isNaN(amount)) amount = 0;
  const config = CURRENCIES[currency] || CURRENCIES.EUR;
  const formatted = amount.toFixed(2).replace(".", ",");
  return config.position === "before" ? `${config.symbol}${formatted}` : `${formatted}${config.symbol}`;
};

const HIGHLIGHT_CONFIG = {
  best_seller:  { label: "🔥 Mais vendido",  classes: "bg-orange-100 text-orange-700" },
  recommended:  { label: "👨‍🍳 Recomendado",  classes: "bg-emerald-100 text-emerald-700" },
  premium:      { label: "⭐ Premium",        classes: "bg-amber-100 text-amber-700" },
};

export function ProductCard({
  product, onAdd, discount, themeColor, priceColor, themeBg, themeText,
  currency = "EUR", highlight = null, locale = "es", autoOpen = false, openCheckout = null,
  restaurantSlug = '', productUrlSlug = '',
}) {
  const pc = themeColor || "#059669";
  const prC = priceColor || pc;
  const isDark = themeBg && parseInt(themeBg.replace('#','').substring(0,2), 16) < 80;
  const cardBg = isDark ? `${themeBg === '#000000' ? '#111111' : themeBg}` : '#ffffff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const textMain = isDark ? (themeText && parseInt(themeText.replace('#','').substring(0,2), 16) > 180 ? themeText : '#f3f4f6') : (themeText || '#111827');
  const textSub = isDark ? 'rgba(255,255,255,0.7)' : '#6b7280';
  const fmt = (amount) => fmtPrice(amount, currency);

  const isUnavailable = product.is_available_now === false;
  const availKey    = product.availability_key    || "";
  const availParams = product.availability_params || {};
  const customLabel = product.availability_label  || "";

  const availabilityMessage = isUnavailable
    ? formatAvailabilityMessage(availKey, availParams, locale, customLabel)
    : "";
  const badgeText = isUnavailable
    ? formatAvailabilityBadge(availKey, availParams, locale)
    : "";

  const productDiscount = product.discount_percentage > 0 ? product.discount_percentage : 0;
  const hasProductOriginalPrice = product.original_price > 0 && product.original_price > product.price;
  const effectiveDiscount = productDiscount || discount || (hasProductOriginalPrice ? Math.round((1 - product.price / product.original_price) * 100) : 0);
  const hasDiscount = (effectiveDiscount > 0 || hasProductOriginalPrice) && !isUnavailable;
  const displayOriginalPrice = hasProductOriginalPrice ? product.original_price : (hasDiscount ? product.price / (1 - effectiveDiscount / 100) : product.price);
  const finalPrice = product.price;

  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    if (!autoOpen) return;
    const el = document.getElementById(`product-deeplink-${product.id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setShowDetail(true);
  }, [autoOpen]); // eslint-disable-line

  const varGroups = product.variation_groups || [];
  const hasVariations = varGroups.length > 0;
  const totalItemCount = varGroups.reduce((sum, g) => sum + (g.items?.length || 0), 0);

  return (
    <>
      <div
        id={`product-deeplink-${product.id}`}
        className={`group rounded-xl border overflow-hidden transition-all duration-200 ${isUnavailable ? "opacity-60 grayscale-[50%] cursor-default" : "hover:shadow-md cursor-pointer"}`}
        style={{
          backgroundColor: cardBg,
          borderColor: isUnavailable ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)') : cardBorder,
        }}
        onClick={() => {
          setShowDetail(true);
          trackViewProduct({ product_id: product.id, metadata: { product_name: product.name, price: product.price } });
          bcTrackProductView(product.id, { metadata: { product_name: product.name } });
          if (restaurantSlug) {
            const urlSlug = productUrlSlug || slugify(product.name);
            window.history.replaceState({}, '', `/m/${restaurantSlug}/product/${urlSlug}`);
          }
        }}
        data-testid={`product-card-${product.id}`}
      >
        <div className="flex">
          <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between min-h-[100px] sm:min-h-[130px]">
            <div>
              {highlight && HIGHLIGHT_CONFIG[highlight] && !isUnavailable && (
                <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-1 ${HIGHLIGHT_CONFIG[highlight].classes}`}
                  data-testid={`badge-${highlight}-${product.id}`}>
                  {HIGHLIGHT_CONFIG[highlight].label}
                </span>
              )}
              {isUnavailable && badgeText && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-1 bg-amber-100 text-amber-700 border border-amber-200"
                  data-testid={`badge-unavailable-${product.id}`}>
                  <Lock className="w-2.5 h-2.5" />
                  {badgeText}
                </span>
              )}
              {product.public_display_only && !isUnavailable && (
                <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-1 bg-orange-100 text-orange-700 border border-orange-200"
                  data-testid={`badge-display-only-${product.id}`}>
                  Solo consumo en restaurante
                </span>
              )}
              <h3 className="font-bold text-[13px] sm:text-sm leading-tight mb-0.5 transition-colors" style={{ color: textMain }}>
                {product.name}
              </h3>
              {product.description && (
                <p className="text-[11px] sm:text-xs line-clamp-2 mb-1.5 leading-relaxed" style={{ color: textSub }}>
                  {product.description}
                </p>
              )}
              {isUnavailable && availabilityMessage && (
                <p className="text-[11px] font-medium mt-0.5 mb-1 leading-snug"
                  style={{ color: isDark ? 'rgba(255,200,100,0.85)' : '#92400e' }}
                  data-testid={`availability-label-${product.id}`}>
                  {availabilityMessage}
                </p>
              )}
              {!isUnavailable && <AllergenBadges allergens={product.allergens} />}
              {product.preparation_time > 0 && !isUnavailable && (
                <span className="inline-flex items-center gap-0.5 text-[10px] mt-0.5" style={{ color: textSub }} data-testid={`prep-time-${product.id}`}>
                  <Clock className="w-2.5 h-2.5" />~{product.preparation_time}min
                </span>
              )}
              {hasVariations && (
                <p className="text-[10px] font-semibold mt-0.5" style={{ color: isUnavailable ? textSub : pc }}>{totalItemCount} opcoes</p>
              )}
            </div>

            <div className="flex items-center justify-between mt-1.5">
              <div className="flex items-center gap-1.5">
                {hasDiscount && <span className="text-[10px] sm:text-xs line-through" style={{ color: textSub }}>{fmt(displayOriginalPrice)}</span>}
                <span className={`font-bold text-sm sm:text-base ${hasDiscount ? "text-red-600" : ""}`}
                  style={!hasDiscount ? { color: isUnavailable ? textSub : prC } : {}}>
                  {fmt(finalPrice)}
                </span>
                {hasDiscount && effectiveDiscount > 0 && (
                  <span className="bg-red-500 text-white text-[9px] sm:text-xs px-1 py-0.5 rounded-full font-bold">
                    -{effectiveDiscount}%
                  </span>
                )}
              </div>
              {!hasVariations && !product.public_display_only && (
                <button className="w-7 h-7 sm:w-8 sm:h-8 rounded-full text-white flex items-center justify-center transition shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: isUnavailable ? (isDark ? '#4b5563' : '#9ca3af') : pc }}
                  disabled={isUnavailable}
                  onClick={(e) => { e.stopPropagation(); if (!isUnavailable) onAdd(product); }}
                  data-testid={`add-product-${product.id}`}>
                  {isUnavailable ? <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                </button>
              )}
            </div>
          </div>

          {(product.image_url || product.video_url) && (
            <ProductMediaThumb product={product} isDark={isDark} />
          )}
        </div>
      </div>

      <ProductDetailModal
        open={showDetail}
        onOpenChange={(v) => {
          setShowDetail(v);
          if (!v && restaurantSlug) window.history.replaceState({}, '', `/m/${restaurantSlug}`);
        }}
        product={product}
        isUnavailable={isUnavailable}
        isDark={isDark} cardBg={cardBg} textMain={textMain} textSub={textSub}
        pc={pc} prC={prC} fmt={fmt}
        hasDiscount={hasDiscount}
        displayOriginalPrice={displayOriginalPrice}
        effectiveDiscount={effectiveDiscount}
        badgeText={badgeText}
        availabilityMessage={availabilityMessage}
        onAdd={onAdd}
        restaurantSlug={restaurantSlug}
        autoOpen={autoOpen}
        openCheckout={openCheckout}
      />
    </>
  );
}
